import React from "react";
import { useNavigate } from "react-router-dom";
import { FaUserCircle } from "react-icons/fa";
import PublishedModeCard from "../cards/PublishedModeCard";

const RailTitle = ({ title, onSeeAll }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="text-lg font-bold">{title}</h2>
    {onSeeAll && (
      <button
        onClick={onSeeAll}
        className="text-primary text-sm font-bold hover:text-mainText transition-colors"
      >
        عرض الكل
      </button>
    )}
  </div>
);

// Horizontal, snap-scrolling row. The page is RTL, so it starts at the right.
const Scroller = ({ children }) => (
  <div className="flex gap-3 overflow-x-auto pb-2 snap-x">{children}</div>
);

const ModesRail = ({ rail, onSeeAll }) => (
  <section>
    <RailTitle title={rail.title?.ar} onSeeAll={rail.seeAll ? onSeeAll : undefined} />
    <Scroller>
      {rail.items.map((mode) => (
        <div key={mode._id} className="snap-start shrink-0 w-44 sm:w-52">
          <PublishedModeCard mode={mode} />
        </div>
      ))}
    </Scroller>
  </section>
);

const HeroRail = ({ rail }) => {
  const navigate = useNavigate();
  return (
    <Scroller>
      {rail.items.map((mode) => (
        <button
          key={mode._id}
          onClick={() => navigate(`/app/modes/${mode._id}`)}
          className="group relative snap-start shrink-0 w-full sm:w-[32rem] h-48 sm:h-60 rounded-2xl overflow-hidden border border-white/10 hover:border-primary/40 bg-white/[0.05] text-right transition-colors"
        >
          {mode.image && (
            <img src={mode.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-header via-header/50 to-transparent" />
          <span className="absolute top-3 right-3 bg-primary text-header text-xs font-bold px-3 py-1 rounded-full shadow">
            {mode.featureTitle?.ar || "اختيار الفريق"}
          </span>
          <div className="absolute bottom-0 inset-x-0 p-4">
            <h3 className="text-white text-xl font-extrabold line-clamp-1">{mode.name}</h3>
            <p className="text-white/70 text-sm line-clamp-1 mt-0.5">
              {mode.owner?.gameName || mode.owner?.name ? `${mode.owner.gameName || mode.owner.name} · ` : ""}
              {mode.questionsCount ?? 0} سؤال · {mode.likesCount ?? 0} إعجاب
            </p>
          </div>
        </button>
      ))}
    </Scroller>
  );
};

const CreatorsRail = ({ rail }) => {
  const navigate = useNavigate();
  return (
    <section>
      <RailTitle title={rail.title?.ar} />
      <Scroller>
        {rail.items.map((creator, index) => (
          <button
            key={creator._id}
            onClick={() => navigate(`/app/users/${creator._id}`)}
            className="relative snap-start shrink-0 w-32 rounded-2xl border border-white/10 bg-white/[0.05] hover:border-white/25 p-3 flex flex-col items-center gap-1 transition-colors"
          >
            <span className="absolute top-2 right-2 bg-primary text-header text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {index + 1}
            </span>
            {creator.image ? (
              <img src={creator.image} alt="" className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <FaUserCircle className="w-14 h-14 text-white/30" />
            )}
            <span className="text-white text-sm font-semibold line-clamp-1">
              {creator.gameName || creator.name}
            </span>
            <span className="text-white/55 text-[11px]">
              {creator.stats?.publishedModes ?? 0} فئة · {creator.stats?.totalLikes ?? 0} إعجاب
            </span>
          </button>
        ))}
      </Scroller>
    </section>
  );
};

export const CategoryChips = ({ title, categories, selected, onSelect }) => {
  if (!categories?.length) return null;
  return (
    <section>
      {title && <RailTitle title={title} />}
      <Scroller>
        {categories.map((c) => (
          <button
            key={c.key}
            onClick={() => onSelect(c.key === selected ? null : c.key)}
            className={`snap-start shrink-0 text-sm font-semibold h-9 px-4 rounded-full border transition-colors ${
              selected === c.key
                ? "bg-primary/15 border-primary/50 text-primary"
                : "bg-white/[0.05] border-white/10 text-white/80 hover:text-white hover:border-white/25"
            }`}
          >
            {c.label?.ar || c.key}
          </button>
        ))}
      </Scroller>
    </section>
  );
};

// Renders one rail of the discover response by its `type`. Unknown types are
// skipped so the server can add rails without breaking this page.
const Rail = ({ rail, onSeeAll, onCategory, selectedCategory }) => {
  if (!rail?.items?.length) return null;
  switch (rail.type) {
    case "hero":
      return <HeroRail rail={rail} />;
    case "modes":
      return <ModesRail rail={rail} onSeeAll={() => onSeeAll?.(rail)} />;
    case "creators":
      return <CreatorsRail rail={rail} />;
    case "categories":
      return (
        <CategoryChips
          title={rail.title?.ar}
          categories={rail.items}
          selected={selectedCategory}
          onSelect={onCategory}
        />
      );
    default:
      return null;
  }
};

export default Rail;
